import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { startWith, Subject, Subscription } from 'rxjs';
import { AppFieldValidators } from 'src/app/utilities/validators';

@Component({
  selector: 'app-base-form',
  template: '',
})
export class BaseFormComponent<T> implements AfterViewInit, OnDestroy {
  private controlCreated = false;
  public featureName: 'saveTank' | null = null;

  public readonly ValueFormats = {
    VolumeFormatting: '1.2',
    HeightFormatting: '1.2',
    DateFormatting: 'MM/dd/yyyy hh:mm a',
    PercentFormatting: '1.1-1',
  };

  public readonly minValue = 0.000000001;
  public readonly maxValue = 2000000000;

  private _formName: string | null = null;
  public get formName(): string | null {
    return this._formName;
  }
  @Input()
  public set formName(value: string) {
    this._formName = value;
    if (this.controlCreated || !this._parentFormGroup || !this._formName)
      return;
    this.setupFormControl();
  }

  protected _formData: T | null;
  public get formData(): T | null {
    return this._formData;
  }
  @Input()
  public set formData(value: T | null) {
    this._formData = value;
  }

  protected _disabled: boolean = false;
  public get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  public set disabled(value: boolean) {
    this._disabled = value;
  }

  protected _parentFormGroup: UntypedFormGroup = null;
  public get parentFormGroup(): UntypedFormGroup {
    return this._parentFormGroup;
  }

  @Input()
  public set parentFormGroup(value: UntypedFormGroup) {
    this._parentFormGroup = value;
    if (!this._parentFormGroup || !this._formName)
      return;

      this.setupFormControl();
  }

  private setupFormControl() {
    //add self to parent form group
    const isParentDisabled = this._parentFormGroup.disabled;

    if(isParentDisabled === true) {
      this.formGroup.disable({emitEvent: false});
    }

    //if the form control already exists, use it
    if (this._parentFormGroup.get(this._formName)) {
      this.formGroup = this._parentFormGroup.get(this._formName) as UntypedFormGroup;
    } else {
      //otherwise, add it to the parent form group
      this._parentFormGroup.addControl(this._formName, this.formGroup);
    }

    //there's a "feature" if you add a control to a disabled formgroup,
    // the formgroup becomes "Valid instead, removing the disabled state entirely.
    // we are attempting to reset the parent form group and current control to
    // disabled if it already was before hand.
    if (isParentDisabled === true) {
      this._parentFormGroup.disable();
    }

    this.controlCreated = true;
  }

  public formGroup: UntypedFormGroup;
  public readonly Validators = AppFieldValidators;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  public readonly TankCapacityValidator = TankCapacityValidator;
  public readonly IntegerValidator = IntegerValidator;
  public readonly FloatValidator = FloatValidator;
  public readonly NumberValidator = NumberValidator;
  public isFormResetting = false;

  @Input() public uniqueId: number | null = null;

  constructor(
    protected _formBuilder: UntypedFormBuilder // private _saveFunction: any
  ) {
    this.buildFormGroup();
  }

  destroy$ = new Subject<boolean>();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    //enable/disable form group based on disabled property
    if (this.disabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  public isDirty() {
    return this.formGroup.dirty;
  }

  public GetLocalizedLenAbbrKey(abbreviationKey: string): string {
    if (!abbreviationKey) return '';

    return 'LengthAbbreviation.' + abbreviationKey;
  }

  public GetLocalizedVolAbbrKey(abbreviationKey: string): string {
    if (!abbreviationKey) return '';

    return 'VolumeAbbreviation.' + abbreviationKey;
  }

  public Multiply(n1: number, n2: number, decimalPlaces: number): string {
    let result: number = n1 * n2;
    return result.toFixed(decimalPlaces);
  }

  public MultiplyPercent(n1: number, pct: number, decimalPlaces: number): string {
    let result: number = n1 * pct / 100;
    return result.toFixed(decimalPlaces);
  }

  emailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {

    if (control.value === null || control.value === undefined) return null;

    const value = control.value as string;
    const valid = value.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g);
    const rtn =  valid ? null : { email: true };
    return rtn;

  }


  private buildFormGroup() {
    this.formGroup = this._formBuilder.group({});
  }

  /** Sets or clears a named error on a form control without clobbering other errors. */
  protected setControlError(controlName: string, errorKey: string, isInvalid: boolean): void {
    const control = this.formGroup?.get(controlName);
    if (!control) {
      return;
    }

    const currentErrors = control.errors ?? {};
    if (isInvalid) {
      control.setErrors({ ...currentErrors, [errorKey]: true });
      if (!control.touched) {
        control.markAsTouched({ onlySelf: true });
      }
      return;
    }

    if (!currentErrors[errorKey]) {
      return;
    }

    const { [errorKey]: _, ...remainingErrors } = currentErrors;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  /** Subscribes to formGroup.valueChanges (starting with current value) and calls applyFn on each change. */
  protected connectValidationOnValueChanges(applyFn: () => void): Subscription {
    return this.formGroup.valueChanges
      .pipe(startWith(this.formGroup.getRawValue()))
      .subscribe(() => applyFn());
  }
}

export function TankCapacityValidator(formGroup: UntypedFormGroup): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined) return null;

    const value = parseFloat(control.value ?? '0');
    const tankCapConverted = parseFloat(
      formGroup.get('tankCapConverted').value ?? '0'
    );
    const valid =
      value <= tankCapConverted ? null : { tankCapacityValidator: true };

    return valid;
  };
}

export function IntegerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined) return null;
    const value = Number.parseFloat(control?.value);
    const valid = Number.isInteger(value);
    const rtn =  valid ? null : { isInt: true };
    return rtn;
  };
}

export function FloatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined) return null;
    const value = Number.parseFloat(control?.value);
    const valid = !Number.isInteger(value);
    const rtn =  valid ? null : { isFloat: true };
    return rtn;
  };
}

export function NumberValidator(): ValidatorFn {

  return (control: AbstractControl): ValidationErrors | null => {

      if (control.value === null || control.value === undefined) return null;

      const value = Number.parseFloat(control?.value);
      const valid = !Number.isNaN(value);
      const rtn =  valid ? null : { isNumber: true };
      return rtn;
    };

  }

